// --- START OF FILE StatsView.js ---

import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { 
    ArrowLeft, Users, ChevronDown, Clock, ShieldCheck, 
    BarChart2, TrendingUp, Sparkles, Target, Tag,
    Check, Ban, Shield, Sun, X, ArrowRightLeft 
} from 'lucide-react';

// --- Reusable Stat Calculation Logic ---
const calculateLifetimeStats = (matchHistory, currentUserName) => {
    const initialStats = {
        totalShots: 0, successfulShots: 0, fouls: 0, totalSafeties: 0, successfulSafeties: 0,
        breakAndRuns: 0, inningsPlayed: 0, gamesWon: 0, gamesLost: 0,
        totalBallsMade: 0,
        shotTypeDistribution: { made: 0, miss: 0, foul: 0, safety: 0, dry_break: 0, push_out: 0 },
        currentStreak: { type: null, count: 0 },
        recentForm: { wins: 0, total: 0 },
        breakStats: {
            attempts: 0, ballsMade: 0, dryBreaks: 0, racksWon: 0,
            potDistribution: {}, scratches: 0, goldenBreaks: 0, continuations: 0
        },
        efficiency: { totalRacks: 0, perfectRacks: 0, runOuts: 0 },
        moneyBallStats: { attempts: 0, made: 0 },
        tagPerformance: {},
        locationStats: {},
    };

    if (!matchHistory || matchHistory.length === 0) {
        return initialStats;
    }

    const reversedHistory = [...matchHistory].reverse();
    for (const match of reversedHistory) {
        // --- MODIFICATION: Use name for consistency ---
        const playerHandle = match.matchData.player1 === currentUserName ? 'player1' : 'player2';
        const won = match.gameState.winner === playerHandle;
        const currentStreakType = won ? 'W' : 'L';
        
        if (initialStats.currentStreak.type === currentStreakType) {
            initialStats.currentStreak.count++;
        } else {
            initialStats.currentStreak = { type: currentStreakType, count: 1 };
        }
    }
    const recentMatches = matchHistory.slice(0, 5);
    initialStats.recentForm.total = recentMatches.length;
    initialStats.recentForm.wins = recentMatches.filter(match => {
        const playerHandle = match.matchData.player1 === currentUserName ? 'player1' : 'player2';
        return match.gameState.winner === playerHandle;
    }).length;

    for (const match of matchHistory) {
        const playerHandle = match.matchData.player1 === currentUserName ? 'player1' : 'player2';
        
        if (match.gameState.winner === playerHandle) initialStats.gamesWon++;
        else initialStats.gamesLost++;

        if (match.matchData.location) {
            const loc = match.matchData.location;
            if(!initialStats.locationStats[loc]) initialStats.locationStats[loc] = { played: 0, won: 0 };
            initialStats.locationStats[loc].played++;
            if (match.gameState.winner === playerHandle) initialStats.locationStats[loc].won++;
        }

        let gameSlices = [];
        if (match.shotLog.length > 0) {
            let currentSlice = [];
            for (const shot of match.shotLog) {
                currentSlice.push(shot);
                if (shot.isGameWinner) {
                    gameSlices.push(currentSlice);
                    currentSlice = [];
                }
            }
            if (currentSlice.length > 0) gameSlices.push(currentSlice);
        }
        
        for (const rack of gameSlices) {
            if (!rack || rack.length === 0) continue;
            const firstShot = rack[0];
            const rackWinner = rack[rack.length - 1].winnerOfRack;

            if (firstShot.tags.includes('break') && firstShot.player === playerHandle) {
                initialStats.breakStats.attempts++;
                initialStats.breakStats.ballsMade += firstShot.ballsMade.length;
                firstShot.ballsMade.forEach(b => { initialStats.breakStats.potDistribution[b] = (initialStats.breakStats.potDistribution[b] || 0) + 1; });
                if (firstShot.result === 'foul') initialStats.breakStats.scratches++;
                if (firstShot.result === 'dry_break') initialStats.breakStats.dryBreaks++;
                // Check for golden break using the golden_break tag
                if (firstShot.tags.includes('golden_break')) {
                    initialStats.breakStats.goldenBreaks++;
                }
                if (rack.length > 1 && rack[1].player === firstShot.player) { initialStats.breakStats.continuations++; }
                if (rackWinner === playerHandle) { initialStats.breakStats.racksWon++; }
            }

            if (rackWinner === playerHandle) {
                const winnerShots = rack.filter(s => s.player === playerHandle);
                const hasError = winnerShots.some(s => ['miss', 'foul', 'safety', 'dry_break', 'push_out'].includes(s.result));
                if (!hasError) initialStats.efficiency.perfectRacks++;
                if (rack.every(s => s.player === playerHandle)) initialStats.efficiency.runOuts++;
            }
            initialStats.efficiency.totalRacks++;
        }
        
        const inningsInMatch = new Set();
        for (let i = 0; i < match.shotLog.length; i++) {
            const shot = match.shotLog[i];
            if (shot.player === playerHandle) {
                initialStats.totalShots++;
                initialStats.totalBallsMade += shot.ballsMade.length;
                if (shot.result === 'made') initialStats.successfulShots++;
                if (shot.result === 'foul') initialStats.fouls++;
                if (shot.result === 'safety') {
                    initialStats.totalSafeties++;
                    const nextShot = match.shotLog[i+1];
                    if (nextShot && nextShot.player !== playerHandle && (nextShot.result === 'miss' || nextShot.result === 'foul' || nextShot.result === 'safety')) {
                        initialStats.successfulSafeties++;
                    }
                }
                if (shot.tags.includes('break_and_run')) initialStats.breakAndRuns++;
                if (initialStats.shotTypeDistribution[shot.result] !== undefined) initialStats.shotTypeDistribution[shot.result]++;
                if (shot.tags.includes('money_ball_shot')) {
                    initialStats.moneyBallStats.attempts++;
                    if (shot.result === 'made') initialStats.moneyBallStats.made++;
                }
                const specialTags = ['jump', 'kick', 'bank', 'masse', 'combo', 'lucky', 'lucky_safe', 'failed_safety'];
                shot.tags.forEach(tag => {
                    if (specialTags.includes(tag)) {
                        if (!initialStats.tagPerformance[tag]) {
                            initialStats.tagPerformance[tag] = { attempts: 0, success: 0 };
                        }
                        initialStats.tagPerformance[tag].attempts++;
                        if (shot.result === 'made') {
                            initialStats.tagPerformance[tag].success++;
                        }
                    }
                });
                inningsInMatch.add(shot.inning);
            }
        }
        initialStats.inningsPlayed += inningsInMatch.size;
    }

    initialStats.winPercentage = (initialStats.gamesWon + initialStats.gamesLost) > 0 ? ((initialStats.gamesWon / (initialStats.gamesWon + initialStats.gamesLost)) * 100).toFixed(1) : '0.0';
    initialStats.pottingPercentage = initialStats.totalShots > 0 ? ((initialStats.successfulShots / initialStats.totalShots) * 100).toFixed(1) : '0.0';
    initialStats.safetySuccessRate = initialStats.totalSafeties > 0 ? ((initialStats.successfulSafeties / initialStats.totalSafeties) * 100).toFixed(1) : '0.0';
    initialStats.breakAndRunRate = initialStats.breakStats.attempts > 0 ? ((initialStats.breakAndRuns / initialStats.breakStats.attempts) * 100).toFixed(1) : '0.0';
    initialStats.clutchPotRate = initialStats.moneyBallStats.attempts > 0 ? ((initialStats.moneyBallStats.made / initialStats.moneyBallStats.attempts) * 100).toFixed(1) : '0.0';
    initialStats.efficiency.avgShotsPerRack = initialStats.efficiency.totalRacks > 0 ? (initialStats.totalShots / initialStats.efficiency.totalRacks).toFixed(1) : '0.0';
    initialStats.efficiency.ballsPerInning = initialStats.inningsPlayed > 0 ? (initialStats.totalBallsMade / initialStats.inningsPlayed).toFixed(1) : '0.0';
    
    for (const loc in initialStats.locationStats) {
        const s = initialStats.locationStats[loc];
        s.winPercentage = s.played > 0 ? ((s.won / s.played) * 100).toFixed(1) : '0.0';
    }

    return initialStats;
};


// --- UI Sub-components ---
const Section = ({ title, icon: Icon, children, defaultOpen = true }) => {
    const [isOpen, setIsOpen] = useState(defaultOpen);
    return (
        <div className="bg-slate-800/50 rounded-xl border border-slate-700">
            <button onClick={() => setIsOpen(!isOpen)} className="w-full text-left p-4 flex justify-between items-center">
                <h3 className="font-bold text-lg flex items-center gap-2">
                    {Icon && <Icon size={20} />} {title}
                </h3>
                <ChevronDown className={`w-5 h-5 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
            </button>
            {isOpen && <div className="p-4 pt-0">{children}</div>}
        </div>
    );
};

const StatCard = ({ title, value, unit = '' }) => (
    <motion.div
        key={`${title}-${value}`}
        initial={{ scale: 0.95, opacity: 0.8 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: 'spring', stiffness: 400, damping: 20 }}
        className="bg-slate-700/50 p-3 rounded-lg text-center"
    >
        <div className="text-sm text-slate-400 mb-1 truncate">{title}</div>
        <div className="text-2xl font-bold text-white">{value}<span className="text-lg text-slate-300">{unit}</span></div>
    </motion.div>
);

const DetailedStat = ({ label, value, total, unit = '%' }) => {
    const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : '0.0';
    return (
        <div className="flex justify-between items-center text-sm py-1.5 px-2 rounded-md bg-slate-700/30">
            <span className="font-medium text-slate-300 capitalize">{label.replace(/_/g, ' ')}</span>
            <span className="font-semibold text-white">
                {value} / {total}
                <span className="text-slate-400 ml-2">({percentage}{unit})</span>
            </span>
        </div>
    );
};

const shotResultsInfo = [
    { value: 'made', label: 'Made', Icon: Check, color: 'bg-green-500' },
    { value: 'miss', label: 'Miss', Icon: X, color: 'bg-red-500' },
    { value: 'foul', label: 'Foul', Icon: Ban, color: 'bg-orange-500' },
    { value: 'safety', label: 'Safety', Icon: Shield, color: 'bg-blue-500' },
    { value: 'dry_break', label: 'Dry Break', Icon: Sun, color: 'bg-gray-500' },
    { value: 'push_out', label: 'Push Out', Icon: ArrowRightLeft, color: 'bg-purple-500' },
];

const formatDate = (timestamp) => {
    if (timestamp && typeof timestamp.toDate === 'function') return new Date(timestamp.toDate()).toLocaleDateString();
    if (timestamp) return new Date(timestamp).toLocaleDateString();
    return 'N/A';
};

export const StatsView = ({ matchHistory, currentUserData, onBack, onReviewMatch, allProfiles }) => {
    const [selectedOpponent, setSelectedOpponent] = useState('All');
    const [selectedGameType, setSelectedGameType] = useState('All');
    const [selectedDateRange, setSelectedDateRange] = useState('All Time');
    
    const opponentList = useMemo(() => {
        const opponents = matchHistory
            .map(m => m.matchData.player1Id === currentUserData.uid ? m.matchData.player2 : m.matchData.player1)
            .filter(name => name !== 'Ghost');
        return ['All', ...[...new Set(opponents)]];
    }, [matchHistory, currentUserData.uid]);

    const filteredHistory = useMemo(() => {
        let history = matchHistory.filter(m => m.matchData.player2Id !== 'ghost');
        if (selectedOpponent !== 'All') {
            history = history.filter(m => (m.matchData.player1 === selectedOpponent || m.matchData.player2 === selectedOpponent));
        }
        if (selectedGameType !== 'All') {
            history = history.filter(m => m.matchData.gameType === selectedGameType);
        }
        if (selectedDateRange !== 'All Time') {
            const now = new Date();
            const daysToSubtract = selectedDateRange === 'Last 7 Days' ? 7 : 30;
            const cutoffDate = new Date(now.setDate(now.getDate() - daysToSubtract));
            history = history.filter(m => new Date(m.completedAt?.toDate() || m.gameState.endTime) >= cutoffDate);
        }
        return history;
    }, [matchHistory, selectedOpponent, selectedGameType, selectedDateRange]);
    
    const practiceMatches = useMemo(() => matchHistory.filter(m => m.matchData.player2Id === 'ghost'), [matchHistory]);
    
    const stats = useMemo(() => calculateLifetimeStats(filteredHistory, currentUserData.name), [filteredHistory, currentUserData.name]);
    const practiceStats = useMemo(() => calculateLifetimeStats(practiceMatches, currentUserData.name), [practiceMatches, currentUserData.name]);

    return (
        <div className="p-2 sm:p-4 max-w-4xl mx-auto pb-8">
            <div className="flex items-center mb-4">
                <button onClick={onBack} className="p-2 rounded-full hover:bg-slate-700 -ml-2"><ArrowLeft className="w-6 h-6" /></button>
                <h1 className="text-2xl sm:text-3xl font-bold ml-4">Performance Stats</h1>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6 bg-slate-800/50 p-4 rounded-xl border border-slate-700">
                <div>
                    <label className="block text-sm font-medium mb-1 text-slate-400">Opponent</label>
                    <select value={selectedOpponent} onChange={e => setSelectedOpponent(e.target.value)} className="w-full bg-slate-700 p-2 rounded-md">
                        {opponentList.map(opp => <option key={opp} value={opp}>{opp}</option>)}
                    </select>
                </div>
                <div>
                    <label className="block text-sm font-medium mb-1 text-slate-400">Game Type</label>
                    <select value={selectedGameType} onChange={e => setSelectedGameType(e.target.value)} className="w-full bg-slate-700 p-2 rounded-md">
                        <option value="All">All</option>
                        <option value="8-Ball">8-Ball</option>
                        <option value="9-Ball">9-Ball</option>
                        <option value="10-Ball">10-Ball</option>
                    </select>
                </div>
                <div>
                    <label className="block text-sm font-medium mb-1 text-slate-400">Date Range</label>
                    <select value={selectedDateRange} onChange={e => setSelectedDateRange(e.target.value)} className="w-full bg-slate-700 p-2 rounded-md">
                        <option>All Time</option><option>Last 7 Days</option><option>Last 30 Days</option>
                    </select>
                </div>
            </div>

            {filteredHistory.length > 0 ? (
                <div className="space-y-6">
                    <Section title={selectedOpponent === 'All' ? "Overall Key Metrics" : `H2H vs. ${selectedOpponent}`} icon={BarChart2} defaultOpen={true}>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                            <StatCard title="Win Rate" value={stats.winPercentage} unit="%" />
                            <StatCard title="Potting %" value={stats.pottingPercentage} unit="%" />
                            <StatCard title="Total Matches" value={filteredHistory.length} />
                            <StatCard title="Total Wins" value={stats.gamesWon} />
                            <StatCard title="Total Losses" value={stats.gamesLost} />
                            <StatCard title="B & R's" value={stats.breakAndRuns} />
                        </div>
                    </Section>

                    <Section title="Advanced Metrics" icon={Sparkles} defaultOpen={true}>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                            <StatCard title="Safety Success" value={stats.safetySuccessRate} unit="%" />
                            <StatCard title="B & R Rate" value={stats.breakAndRunRate} unit="%" />
                            <StatCard title="Clutch Pot %" value={stats.clutchPotRate} unit="%" />
                        </div>
                    </Section>

                    {/* Break Performance Section - Show golden breaks for 9-Ball */}
                    {stats.breakStats.attempts > 0 && (
                        <Section title="Break Performance" icon={Target} defaultOpen={true}>
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                                <StatCard title="Break Attempts" value={stats.breakStats.attempts} />
                                <StatCard title="Break Success Rate" value={stats.breakStats.attempts > 0 ? ((stats.breakStats.attempts - stats.breakStats.dryBreaks) / stats.breakStats.attempts * 100).toFixed(1) : 0} unit="%" />
                                <StatCard title="Avg Balls/Break" value={stats.breakStats.attempts > 0 ? (stats.breakStats.ballsMade / stats.breakStats.attempts).toFixed(1) : 0} />
                                {selectedGameType === '9-Ball' && (
                                    <StatCard title="Golden Breaks" value={stats.breakStats.goldenBreaks} />
                                )}
                                <StatCard title="Dry Breaks" value={stats.breakStats.dryBreaks} />
                                <StatCard title="Break & Runs" value={stats.breakAndRuns} />
                            </div>
                            {selectedGameType === '9-Ball' && stats.breakStats.goldenBreaks > 0 && (
                                <div className="mt-4 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                                    <div className="flex items-center gap-2 text-yellow-400 font-semibold">
                                        <span className="text-2xl">👑</span>
                                        <span>Golden Break Rate: {stats.breakStats.attempts > 0 ? ((stats.breakStats.goldenBreaks / stats.breakStats.attempts) * 100).toFixed(1) : 0}%</span>
                                    </div>
                                    <p className="text-sm text-slate-400 mt-1">
                                        {stats.breakStats.goldenBreaks} golden break{stats.breakStats.goldenBreaks !== 1 ? 's' : ''} out of {stats.breakStats.attempts} break attempt{stats.breakStats.attempts !== 1 ? 's' : ''}
                                    </p>
                                </div>
                            )}
                        </Section>
                    )}
                    
                    <Section title="Recent Form" icon={TrendingUp} defaultOpen={true}>
                        <div className="grid grid-cols-2 gap-3">
                            <div className="bg-slate-700/50 p-3 rounded-lg text-center">
                                <div className="text-sm text-slate-400 mb-1">Current Streak</div>
                                <div className={`text-2xl font-bold ${stats.currentStreak.type === 'W' ? 'text-green-400' : 'text-red-400'}`}>{stats.currentStreak.count} {stats.currentStreak.type}</div>
                            </div>
                            <div className="bg-slate-700/50 p-3 rounded-lg text-center">
                                <div className="text-sm text-slate-400 mb-1">Last {stats.recentForm.total} Matches</div>
                                <div className="text-2xl font-bold text-white">{stats.recentForm.wins} <span className="text-slate-400">W</span> - {stats.recentForm.total - stats.recentForm.wins} <span className="text-slate-400">L</span></div>
                            </div>
                        </div>
                    </Section>

                    <Section title="Shot Distribution" defaultOpen={true}>
                        <div className="space-y-3">
                            {Object.entries(stats.shotTypeDistribution).map(([type, count]) => {
                                if(count === 0) return null;
                                const resultInfo = shotResultsInfo.find(r => r.value === type);
                                const percentage = stats.totalShots > 0 ? (count / stats.totalShots * 100).toFixed(0) : 0;
                                return (
                                    <div key={type} className="text-sm">
                                        <div className="flex justify-between items-center mb-1 font-medium"><span className="capitalize">{resultInfo?.label || type.replace(/_/g, ' ')}</span><span>{count} <span className="text-slate-400">({percentage}%)</span></span></div>
                                        <div className="w-full bg-slate-600 rounded-full h-2"><div className={`${resultInfo?.color || 'bg-gray-400'} h-2 rounded-full`} style={{width: `${percentage}%`}}></div></div>
                                    </div>
                                )
                            })}
                        </div>
                   </Section>

                   <Section title="Situational Performance" defaultOpen={true}>
                        <div className="space-y-4">
                            <h4 className="font-semibold text-slate-300 mb-2 flex items-center gap-2"><Tag size={16}/> Shot Type Performance</h4>
                            <div className="space-y-2">
                                {Object.keys(stats.tagPerformance).length > 0 ? Object.entries(stats.tagPerformance).map(([tag, data]) => (
                                    <DetailedStat key={tag} label={tag} value={data.success} total={data.attempts} />
                                )) : <p className="text-sm text-slate-400 text-center">No special shots tagged yet.</p>}
                            </div>
                        </div>
                   </Section>

                    <Section title="Practice vs. Ghost" icon={Sparkles} defaultOpen={true}>
                        <div className="grid grid-cols-2 gap-3">
                            <StatCard title="Practice Win Rate" value={practiceStats.winPercentage} unit="%" />
                            <StatCard title="Practice W-L" value={`${practiceStats.gamesWon} - ${practiceStats.gamesLost}`} />
                            <StatCard title="Total Practice" value={practiceMatches.length} />
                            <StatCard title="Practice Shots" value={practiceStats.totalShots} />
                        </div>
                    </Section>

                    <Section title="Filtered Match History" icon={Users} defaultOpen={true}>
                        <div className="space-y-2 max-h-96 overflow-y-auto pr-2">
                            {filteredHistory.map(match => {
                                const isPlayer1 = match.matchData.player1Id === currentUserData.uid;
                                const opponentName = isPlayer1 ? match.matchData.player2 : match.matchData.player1;
                                const myScore = isPlayer1 ? match.gameState.player1Score : match.gameState.player2Score;
                                const opponentScore = isPlayer1 ? match.gameState.player2Score : match.gameState.player1Score;
                                const isWin = match.gameState.winner === (isPlayer1 ? 'player1' : 'player2');
                                
                                return (
                                    <button key={match.id} onClick={() => onReviewMatch(match)} disabled={match.matchData.trackingMode === 'simple'} className={`w-full text-left bg-slate-700/50 p-3 rounded-lg transition-colors disabled:cursor-not-allowed hover:bg-slate-700/70`}>
                                        <div className="flex justify-between items-center">
                                            <div>
                                                <p className="font-bold">{match.matchData.gameType}</p>
                                                <p className="text-xs text-slate-400">vs. {opponentName}</p>
                                            </div>
                                            <div className="font-semibold text-xl">{myScore} - {opponentScore}</div>
                                            <div className="flex flex-col items-end">
                                                <div className={`font-bold text-lg ${isWin ? 'text-green-400' : 'text-red-400'}`}>{isWin ? 'WIN' : 'LOSS'}</div>
                                                <div className="flex items-center gap-1.5 text-xs text-slate-500 mt-1">
                                                    {match.gameState.forfeited && <span className="font-semibold text-yellow-500">Forfeit</span>}
                                                    <Clock size={12}/>
                                                    <span>{formatDate(match.completedAt)}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    </Section>
                </div>
            ) : (
                <div className="text-center bg-slate-800/50 p-8 rounded-xl border border-slate-700 mt-8">
                    <h3 className="text-xl font-bold">No Match Data Found</h3>
                    <p className="text-slate-400 mt-2">No matches found with the selected filters. Try a different combination!</p>
                </div>
            )}
        </div>
    );
};